import React, { useState, useEffect, useRef } from 'react';
import gsap from 'gsap';
import {
  X,
  ChevronRight,
  ChevronLeft,
  Menu as MenuIcon,
  User,
} from 'lucide-react';
import { clsx } from 'clsx';
import { Link } from 'react-router-dom';

import DesktopLogo from "@/assets/desktopLogo.png";

import ProtectionIcon from "@/assets/header/protection.png";
import ShippingIcon from "@/assets/header/shipping.png";
import ClaimsIcon from "@/assets/header/claims.png";
import FraudIcon from "@/assets/header/fraud.png";
import ReturnsIcon from "@/assets/header/returns.png";

const iconMap: Record<string, string> = {
  Protection: ProtectionIcon,
  Shipping: ShippingIcon,
  Claims: ClaimsIcon,
  Fraud: FraudIcon,
  Returns: ReturnsIcon,
};

// Mobile menu sections structure
const SECTIONS = [
  {
    key: "merchants",
    label: "For Merchants",
    children: [
      {
        key: "platform",
        label: "Our Platform",
        sub: [
          { text: "Platform Overview", href: "/overview" },
          { text: "Protection", href: "/protection" },
          { text: "Shipping", href: "/shipping" },
          { text: "Claims", href: "/claims" },
          { text: "Fraud", href: "/fraud" },
          { text: "Returns", href: "/returns" },
        ],
      },
      // {
      //   key: "industries",
      //   label: "Industries",
      //   sub: [
      //     { text: "Apparel & Accessories", href: "" },
      //     { text: "Automotive", href: "" },
      //     { text: "Electronics", href: "" },
      //     { text: "Furniture", href: "" },
      //     { text: "Health & Beauty", href: "" },
      //     { text: "Home Fitness", href: "" },
      //     { text: "Jewelry", href: "" },
      //     { text: "Mattress", href: "" },
      //     { text: "Sports & Outdoor", href: "" },
      //   ],
      // },
      {
        key: "resources",
        label: "Resources",
        sub: [
          { text: "Articles", href: "/articles" },
          { text: "Case Studies", href: "/case-studies" },
          // { text: "Marketing Toolkit", href: "" },
          // { text: "For Developers", href: "" },
        ],
      },
      {
        key: "partners",
        label: "Our Partners",
        sub: [
          { text: "Shopify", href: "" },
        ],
      },
    ],
  },
  {
    key: "customers",
    label: "For Customers",
    children: [
      {
        key: "claims",
        label: "Claims",
        sub: [
          { text: "File a Claim", href: "/customer/claims" },
          { text: "Login to Protega", href: "/customer-login" },
        ],
      },
      // {
      //   key: "support",
      //   label: "Get Help",
      //   sub: [
      //     { text: "How It Works", href: "" },
      //     { text: "Contact Support", href: "" },
      //   ],
      // },
    ],
  },
  // {
  //   key: "company",
  //   label: "Company",
  //   children: [
  //     {
  //       key: "explore",
  //       label: "Explore Protega",
  //       sub: [
  //         { text: "News & Press", href: "" },
  //         // { text: "Life at Protega", href: "" },
  //         // { text: "Become a Service Provider", href: "" },
  //       ],
  //     },
  //   ],
  // },
];

const Header: React.FC = () => {
  const [hidden, setHidden] = useState(false);
  const [openDropdown, setOpenDropdown] = useState<number | null>(null);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [loginOpen, setLoginOpen] = useState(false);
  const [loginFromMenu, setLoginFromMenu] = useState(false);
  const [openSubmenu, setOpenSubmenu] = useState<string | null>(null);

  const headerRef = useRef<HTMLDivElement | null>(null);
  const dropdownRefs = useRef<Array<HTMLDivElement | null>>([]);
  const dropdownTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const handleMouseEnter = (i: number) => {
    if (dropdownTimeoutRef.current) {
      clearTimeout(dropdownTimeoutRef.current);
      dropdownTimeoutRef.current = null;
    }
    setOpenDropdown(i);
  };

  const handleMouseLeave = () => {
    dropdownTimeoutRef.current = setTimeout(() => {
      setOpenDropdown(null);
    }, 300);
  };

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (headerRef.current && !headerRef.current.contains(event.target as Node)) {
        setOpenDropdown(null);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  // Lock body scroll when either drawer is open
  useEffect(() => {
    if (mobileOpen || loginOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [mobileOpen, loginOpen]);

  useEffect(() => {
    let lastY = window.scrollY;
    const handleScroll = () => {
      const y = window.scrollY;
      if (y > lastY && y > 100) setHidden(true);
      else if (y < lastY) setHidden(false);
      lastY = y;
      setOpenDropdown(null);
    };
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    dropdownRefs.current.forEach((el, idx) => {
      if (!el) return;
      // kill any stray tweens
      gsap.killTweensOf(el);

      const isOpen = openDropdown === idx;

      // one timeline, same ease & duration for in/out
      const tl = gsap.timeline();
      tl.to(el, {
        autoAlpha: isOpen ? 1 : 0,
        yPercent: isOpen ? 0 : -100,
        duration: 0.4,
        ease: 'power2.inOut',
      });

      // pointerEvents only when visible
      tl.set(el, { pointerEvents: isOpen ? 'auto' : 'none' }, 0);
    });
  }, [openDropdown]);

  // on mount
  useEffect(() => {
    dropdownRefs.current.forEach(el => {
      if (!el) return;
      gsap.set(el, { autoAlpha: 0, yPercent: -100, pointerEvents: 'none' });
    });
  }, []);



  // Toggle main mobile menu; close login drawer if opening
  const toggleMobile = () => {
    setMobileOpen((prev) => {
      const next = !prev;
      if (next) {
        setLoginOpen(false);
        setLoginFromMenu(false);
      }
      return next;
    });

    setOpenSubmenu(null);
  };

  // Toggle login drawer; track if opened from within mobile menu
  const toggleLogin = () => {
    setLoginOpen((prev) => {
      const next = !prev;
      if (next) {
        if (mobileOpen) {
          setLoginFromMenu(true);
          setMobileOpen(false);
        } else {
          setLoginFromMenu(false);
        }
      } else {
        setLoginFromMenu(false);
      }
      return next;
    });

    setOpenSubmenu(null);
  };

  // When clicking back from login to menu:
  const goBackToMenu = () => {
    setLoginOpen(false);
    setLoginFromMenu(false);
    setMobileOpen(true);
  };


  const toggleSubmenu = (key: string) =>
    setOpenSubmenu((prev) => (prev === key ? null : key));

  // Desktop dropdown menus
  const menus = [
    {
      label: "For Merchants",
      cols: 4,
      content: (
        // Wrap content in a container with max-height and overflow-y-auto so it scrolls if too tall
        <div className="tw-max-h-[80vh] tw-overflow-y-auto">
          <div className="tw-grid tw-grid-cols-4 tw-gap-8 tw-p-8 tw-max-w-7xl tw-mx-auto">
            {/* Column 1 */}
            <div>
              <h3 className="tw-text-xs tw-text-gray-400 tw-uppercase tw-mb-4">
                Our Platform
              </h3>
              <div>
                {[
                  { text: "Platform Overview", isTitle: true, href: "/overview" },
                  {
                    text: "Protection",
                    description: "Coverage for accidents and more",
                    icon: iconMap.Protection,
                    color: "hover:tw-text-[#00C5FB]",
                    href: "/protection",
                  },
                  {
                    text: "Shipping",
                    description: "Protection for loss, damage, and theft",
                    icon: iconMap.Shipping,
                    color: "hover:tw-text-[#00ffbd]",
                    href: "/shipping",
                  },
                  {
                    text: "Claims",
                    description: "Automated claim support",
                    icon: iconMap.Claims,
                    color: "hover:tw-text-[#ffba33]",
                    href: "/claims",
                  },
                  {
                    text: "Fraud",
                    description: "AI-powered fraud detection",
                    icon: iconMap.Fraud,
                    color: "hover:tw-text-[#ff3333]",
                    href: "/fraud",
                  },
                  {
                    text: "Returns",
                    description: "Seamless returns and exchanges",
                    icon: iconMap.Returns,
                    color: "hover:tw-text-[#d357ff]",
                    href: "/returns",
                  },
                ].map((item) =>
                  item.isTitle ? (

                    <div
                      key={item.text}
                      className="tw-text-xl tw-font-semibold tw-text-gray-300 tw-mb-6 hover:tw-text-[#00C5FB]"
                    >
                      <Link to={item.href}>
                        {item.text}
                      </Link>
                    </div>
                  ) : (
                    <Link
                      key={item.text}
                      to={item.href}
                      className={`tw-flex tw-items-center tw-gap-3 tw-text-gray-300 ${item.color} tw-mb-6`}
                    >
                      <img
                        src={item.icon}
                        alt={`${item.text} icon`}
                        className="tw-h-auto"
                      />
                      <div>
                        <div className="tw-text-xl tw-font-semibold">{item.text}</div>
                        <div className="tw-text-xs tw-text-gray-500">
                          {item.description}
                        </div>
                      </div>
                    </Link>
                  )
                )}
              </div>
            </div>
            {/* Column 2 */}
            {/* <div>
              <h3 className="tw-text-xs tw-text-gray-400 tw-uppercase tw-mb-4">
                Industries
              </h3>
              {[
                { text: "Apparel & Accessories", href: "" },
                { text: "Automotive", href: "" },
                { text: "Electronics", href: "" },
                { text: "Furniture", href: "" },
                { text: "Health & Beauty", href: "" },
                { text: "Home Fitness", href: "" },
                { text: "Jewelry", href: "" },
                { text: "Mattress", href: "" },
                { text: "Sports & Outdoor", href: "" },
              ].map((item) => (
                <a
                  key={item.text}
                  href={item.href}
                  className="tw-block tw-text-gray-300 hover:tw-text-[#00C5FB] tw-mb-2"
                >
                  {item.text}
                </a>
              ))}
            </div> */}
            {/* Column 3 */}
            <div>
              <h3 className="tw-text-xs tw-text-gray-400 tw-uppercase tw-mb-4">
                Resources
              </h3>
              {[
                { text: "Articles", href: "/articles" },
                { text: "Case Studies", href: "/case-studies" },
                // { text: "Marketing Toolkit", href: "" },
                // { text: "For Developers", href: "" },
              ].map((item) => (
                <Link
                  key={item.text}
                  to={item.href}
                  className="tw-block tw-text-gray-300 hover:tw-text-[#00C5FB] tw-mb-2"
                >
                  {item.text}
                </Link>
              ))}
            </div>
            {/* Column 4 */}
            {/* <div>
              <h3 className="tw-text-xs tw-text-gray-400 tw-uppercase tw-mb-4">
                Our Partners
              </h3>
              {[{ text: "Shopify", href: "" }].map((item) => (
                <Link
                  key={item.text}
                  to={item.href}
                  className="tw-block tw-text-gray-300 hover:tw-text-[#00C5FB] tw-mb-2"
                >
                  {item.text}
                </Link>
              ))}
            </div> */}
          </div>
        </div>
      ),
    },
    {
      label: "For Customers",
      cols: 2,
      content: (
        <div className="tw-max-h-[80vh] tw-overflow-y-auto">
          <div className="tw-grid tw-grid-cols-2 tw-gap-8 tw-p-8 tw-max-w-7xl tw-mx-auto">
            <div>
              <h3 className="tw-text-xs tw-text-gray-400 tw-uppercase tw-mb-4">
                Claims
              </h3>
              {[{ text: "File a Claim", href: "/customer/claims" }, { text: "Login to Protega", href: "/customer-login" }].map((item) => (
                <Link
                  key={item.text}
                  to={item.href}
                  target='_blank'
                  className="tw-block tw-text-xl tw-font-semibold tw-text-gray-300 hover:tw-text-[#00C5FB] tw-mb-2"
                >
                  {item.text}
                </Link>
              ))}
            </div>
            <div>
              <h3 className="tw-text-xs tw-text-gray-400 tw-uppercase tw-mb-4">
                Support
              </h3>
              {/* {[{ text: "How It Works", href: "" }, { text: "Contact Support", href: "" }].map((item) => ( */}
              {[{ text: "Contact Support", href: "/contact-us" }].map((item) => (
                <Link
                  key={item.text}
                  to={item.href}
                  className="tw-block tw-text-sm tw-text-gray-300 hover:tw-text-[#00C5FB] tw-mb-2"
                >
                  {item.text}
                </Link>
              ))}
            </div>
          </div>
        </div>
      ),
    },
    // {
    //   label: "Company",
    //   cols: 3,
    //   content: (
    //     <div className="tw-max-h-[80vh] tw-overflow-y-auto">
    //       <div className="tw-grid tw-grid-cols-3 tw-gap-8 tw-p-8 tw-max-w-7xl tw-mx-auto">
    //         <div>
    //           <h3 className="tw-text-xs tw-text-gray-400 tw-uppercase tw-mb-4">
    //             Explore Protega
    //           </h3>
    //           {[
    //             { text: "News & Press", href: "" },

    //           ].map((item) => (
    //             <Link
    //               key={item.text}
    //               to={item.href}
    //               className="tw-block tw-text-xl tw-font-semibold tw-text-gray-300 hover:tw-text-[#00C5FB] tw-mb-2"
    //             >
    //               {item.text}
    //             </Link>
    //           ))}
    //         </div>
    //         <div>
    //           <h3 className="tw-text-xs tw-text-gray-400 tw-uppercase tw-mb-4">
    //             Join Us
    //           </h3>
    //           {[{ text: "Careers", href: "" }, { text: "Life at Protega", href: "" }, { text: "Become a Partner", href: "" }].map((item) => (
    //             <Link
    //               key={item.text}
    //               to={item.href}
    //               className="tw-block tw-text-sm tw-text-gray-300 hover:tw-text-[#00C5FB] tw-mb-2"
    //             >
    //               {item.text}
    //             </Link>
    //           ))}
    //         </div>
    //         <div>
    //           <h3 className="tw-text-xs tw-text-gray-400 tw-uppercase tw-mb-4">
    //             Talk to Us
    //           </h3>
    //           {[
    //             { text: "Schedule a Demo", href: "/schedule-demo" },
    //             { text: "Customer Care", href: "" },
    //             { text: "Merchant Support", href: "" },
    //           ].map((item) => (
    //             <Link
    //               key={item.text}
    //               to={item.href}
    //               className="tw-block tw-text-sm tw-text-gray-300 hover:tw-text-[#00C5FB] tw-mb-2"
    //             >
    //               {item.text}
    //             </Link>
    //           ))}
    //         </div>
    //       </div>
    //     </div>
    //   ),
    // },
    {
      label: "Login",
      cols: 1,
      content: (
        <div className="tw-max-h-[80vh] tw-overflow-y-auto">
          <div className="tw-grid tw-grid-cols-3 tw-gap-8 tw-p-8 tw-max-w-7xl tw-mx-auto">
            <div>
              <h3 className="tw-text-xs tw-text-gray-400 tw-uppercase tw-mb-4">
                Login
              </h3>
              {[{ text: "Customer Login", href: "/customer-login" }, { text: "Merchant Login", href: "https://merchants.protega.ai" }].map((item) => (
                <Link
                  key={item.text}
                  to={item.href}
                  className="tw-block tw-text-xl tw-font-semibold tw-text-gray-300 hover:tw-text-[#00C5FB] tw-mb-2"
                >
                  {item.text}
                </Link>
              ))}
            </div>
          </div>
        </div>
      ),
    },
  ];

  return (
    <>
      {/* Header */}
      <header
        ref={headerRef}
        className={clsx(
          "tw-fixed tw-left-0 tw-max-w-full tw-w-screen tw-right-0 tw-z-50 tw-transition-transform tw-duration-300",
          hidden ? "-tw-translate-y-full" : "tw-translate-y-0",
          openDropdown !== null
            ? "tw-bg-[#001F24]"
            : "tw-bg-[#111111]/90 tw-backdrop-blur-sm"
        )}
      >
        <div className="tw-max-w-8xl tw-mx-auto tw-h-24 tw-px-6 md:tw-px-12 tw-flex tw-items-center">
          <div className="tw-flex tw-items-center tw-justify-between tw-w-full">
            <div className="tw-flex">
              {/* Logo */}
              <Link to="/" className="tw-flex tw-items-center">
                <img
                  src={DesktopLogo}
                  alt="Desktop Logo"
                  className="tw-h-12 tw-block"
                />
              </Link>

              {/* Desktop nav */}
              <nav className="tw-hidden lg:tw-flex tw-items-center tw-space-x-8 lg:-ml-10 tw-h-full">
                {menus.map((m, i) => (
                  <div
                    key={i}
                    className="tw-group tw-relative tw-h-full tw-flex tw-items-center"
                    onMouseEnter={() => handleMouseEnter(i)}
                    onMouseLeave={handleMouseLeave}
                  >
                    <button
                      // onClick={() => toggleDropdown(i)}
                      className={clsx(
                        "tw-flex tw-items-center tw-space-x-1 tw-text-gray-300 hover:tw-text-[#00C5FB] tw-transition-colors tw-py-2",
                        openDropdown === i && "tw-text-white"
                      )}
                    >
                      <span className="tw-uppercase tw-text-sm tw-tracking-wider tw-cursor-pointer">{m.label}</span>
                    </button>
                    <div
                      ref={el => (dropdownRefs.current[i] = el!)}
                      className="tw-fixed tw-top-full tw-left-1/2 tw-w-screen tw--translate-x-1/2 tw-bg-[#001F24] tw-rounded-b-lg tw-shadow-lg"
                    >
                      {m.content}
                    </div>
                  </div>

                ))}
              </nav>
            </div>

            {/* Schedule Demo & Mobile menu icons */}
            <div className="tw-flex tw-items-center tw-space-x-4">
              <Link
                to="/customer/claims"
                target='_blank'
                className="tw-px-4 tw-py-2 tw-rounded-3xl tw-border tw-border-white tw-text-white hover:tw-bg-[#00c5fb] hover:tw-text-black hover:tw-border-[#00c5fb] tw-transition-colors tw-duration-700 tw-font-medium lg:tw-block tw-hidden"
              >
                File a Claim
              </Link>
              <Link
                to="/schedule-demo"

                className="tw-px-4 tw-py-2 tw-rounded-3xl tw-border tw-border-white tw-text-white hover:tw-bg-[#00c5fb] hover:tw-text-black hover:tw-border-[#00c5fb] tw-transition-colors tw-duration-700 tw-font-medium lg:tw-block tw-hidden"
              >
                Schedule a Demo
              </Link>
              {/* User icon toggles login drawer */}
              <button
                onClick={toggleLogin}
                className="tw-block lg:tw-hidden"
                aria-label="Open login"
              >
                <User className="tw-w-6 tw-h-6 tw-text-gray-300 hover:tw-text-[#00C5FB]" />
              </button>
              {/* Hamburger toggles main mobile menu */}
              <button
                onClick={toggleMobile}
                className="tw-block lg:tw-hidden"
                aria-label="Open menu"
              >
                <MenuIcon className="tw-w-6 tw-h-6 tw-text-gray-300 hover:tw-text-[#00C5FB]" />
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* Mobile Menu Drawer */}
      <div
        className={clsx(
          'tw-fixed tw-top-0 tw-right-0 tw-h-full tw-w-4/5 tw-max-w-xs tw-bg-[#001F24] tw-text-gray-200 tw-shadow-lg tw-transform tw-transition-transform tw-duration-500 tw-ease-in-out tw-z-50 lg:tw-hidden',
          mobileOpen ? 'tw-translate-x-0' : 'tw-translate-x-full'
        )}
      >
        <div className="tw-flex tw-flex-col tw-h-full">
          {/* Header bar: Home, User icon, Close */}
          <div className="tw-flex tw-items-center tw-justify-between tw-p-4 tw-shrink-0">
            <span className="tw-text-xl tw-text-white">Home</span>
            <div className="tw-flex tw-items-center tw-space-x-4">
              <button
                onClick={toggleLogin}
                aria-label="Open login"
                className="tw-text-gray-300 hover:tw-text-[#00C5FB]"
              >
                <User className="tw-w-6 tw-h-6" />
              </button>
              <X
                onClick={toggleMobile}
                className="tw-w-6 tw-h-6 tw-cursor-pointer tw-text-gray-300 hover:tw-text-[#00C5FB]"
              />
            </div>
          </div>

          {/* Scrollable menu list */}
          <nav className="tw-flex-1 tw-overflow-y-auto">
            {SECTIONS.map((sec) => (
              <div key={sec.key} className="tw-mb-4">
                <div className="tw-px-4">
                  <span className="tw-text-xs tw-text-gray-400 tw-uppercase">
                    {sec.label}
                  </span>
                </div>

                <div className="tw-mt-2">
                  {sec.children.map((child) => {
                    const isOpen = openSubmenu === child.key;
                    return (
                      <div key={child.key}>
                        {/* Always a toggle button */}
                        <button
                          onClick={() => toggleSubmenu(child.key)}
                          className="tw-w-full tw-flex tw-justify-between tw-items-center tw-py-3 tw-px-4 tw-text-base tw-font-semibold tw-text-white hover:tw-text-[#00C5FB] tw-uppercase"
                        >
                          <span>{child.label}</span>
                          <ChevronRight
                            className={clsx(
                              "tw-w-5 tw-h-5 tw-transform tw-transition-transform tw-duration-300",
                              isOpen ? "tw-rotate-90" : ""
                            )}
                          />
                        </button>

                        {/* Submenu items with icons */}
                        {isOpen && (
                          <ul className="tw-pl-8 tw-mt-1 tw-space-y-1">
                            {child.sub.map((item) => {
                              const iconSrc = iconMap[item.text];
                              return (
                                <li
                                  key={item.text}
                                  className="tw-flex tw-items-center"
                                >
                                  {iconSrc && (
                                    <img
                                      src={iconSrc}
                                      alt={`${item.text} icon`}
                                      className="tw-h-5 tw-w-5 tw-mr-2"
                                    />
                                  )}
                                  <Link
                                    to={item.href}
                                    className="tw-block tw-text-base tw-text-gray-300 hover:tw-text-[#00C5FB] tw-py-2"
                                    onClick={toggleMobile}
                                  >
                                    {item.text}
                                  </Link>
                                </li>
                              );
                            })}
                          </ul>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            ))}
          </nav>

          {/* Footer with buttons fixed at bottom */}
          <div className="tw-p-4 tw-border-t tw-border-gray-700 tw-shrink-0">
            <Link
              to="/customer/claims"
              target='_blank'
              className="tw-block tw-w-full tw-text-center tw-py-3 tw-border tw-border-white tw-rounded-full tw-text-white hover:tw-text-[#00C5FB] tw-mb-3"
              onClick={toggleMobile}
            >
              File a Claim
            </Link>
            <Link
              to="/schedule-demo"
              className="tw-block tw-w-full tw-text-center tw-py-3 tw-border tw-border-white tw-rounded-full tw-text-white hover:tw-text-[#00C5FB]"
              onClick={toggleMobile}
            >
              Schedule a Demo
            </Link>
          </div>
        </div>
      </div>


      {/* Login Drawer */}
      <div
        className={clsx(
          'tw-fixed tw-top-0 tw-right-0 tw-h-full tw-w-4/5 tw-max-w-xs tw-bg-[#001F24] tw-text-gray-200 tw-shadow-lg tw-transform tw-transition-transform tw-duration-500 tw-ease-in-out tw-z-50 lg:tw-hidden',
          loginOpen ? 'tw-translate-x-0' : 'tw-translate-x-full'
        )}
      >
        {/* Improved Header bar for Login */}
        <div className="tw-flex tw-items-center tw-justify-between tw-p-4">
          <div className='tw-flex'>
            {loginFromMenu ? (
              <button
                onClick={goBackToMenu}
                className="tw-text-gray-300 hover:tw-text-[#00C5FB]"
                aria-label="Back to menu"
              >
                <ChevronLeft className="tw-w-6 tw-h-6" />
              </button>
            ) : (
              // placeholder to keep title centered if no back button
              <div className="tw-w-6" />
            )}
            <span className="tw-text-base tw-font-semibold tw-text-white tw-pl-2">Login</span>
          </div>

          {/* Close icon */}
          <X
            onClick={toggleLogin}
            className="tw-w-6 tw-h-6 tw-cursor-pointer tw-text-gray-300 hover:tw-text-[#00C5FB]"
          />
        </div>
        {/* Login options */}
        <nav className="tw-p-4 tw-flex tw-flex-col tw-space-y-4">
          <Link
            to="/customer-login"
            className="tw-block tw-w-full tw-text-center tw-py-3 tw-border tw-border-white tw-rounded-full tw-text-white hover:tw-text-[#00C5FB]"
            onClick={toggleLogin}
          >
            Customer Login
          </Link>
          <a
            href='https://merchants.protega.ai/'
            target='_blank'
            className="tw-block tw-w-full tw-text-center tw-py-3 tw-border tw-border-white tw-rounded-full tw-text-white hover:tw-text-[#00C5FB]"
            onClick={toggleLogin}
          >
            Merchant Login
          </a>
        </nav>
      </div>


      {/* Shared Backdrop */}
      {(mobileOpen || loginOpen) && (
        <div
          className="tw-fixed tw-inset-0 tw-bg-black tw-bg-opacity-40 tw-backdrop-blur-sm tw-z-40 lg:tw-hidden"
          onClick={() => {
            if (mobileOpen) {
              setMobileOpen(false);
              setOpenSubmenu(null);
            }
            if (loginOpen) {
              setLoginOpen(false);
              setLoginFromMenu(false);
            }
          }}
        />
      )}
    </>
  );
};

export default Header;
